import React, { useState } from "react";
import {
  CitationDataFragment,
  ConnectionStrength,
  GroundingFragment,
  GroundingStatements,
  NodeMatch,
  PairGraphEdges,
  StatementHypothesis,
} from "fe-common/graphql";
import {
  Button,
  List,
  ListItemText,
  styled,
  Typography,
  Popper,
  ClickAwayListener,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";

import * as toposort from "toposort";
import { ClinicColors } from "../styles/clinicTheme";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { Theme } from "@mui/material/styles";
import { ReactComponent as ValidatedIcon } from "../assets/patientCard/validatedIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/patientCard/checkCircleIcon.svg";
import WarningIcon from "@mui/icons-material/Warning";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { SafeCollapse } from "../transitions/safeCollapse";

//region [[ Styles ]]

type ConncectionStrength = {
  severity: ErrorSeverityLevel;
  message?: string;
};

enum Validity {
  valid,
  invalid,
  hidden,
}

interface ToolTipProps {
  supNumberIndex: number;
  statement: GroundingStatements;
  textualData: string[];
  references: string[];
  connectionStrength: ConncectionStrength;
  onClose?: () => void;
  outputReferenceSection?: boolean;
  inExtension: boolean;
}

const ValidateStyle = styled("i")({
  cursor: "pointer",
  fontStyle: "normal",
  "&.numeric-ref": {
    verticalAlign: "super",
    textDecoration: "underline",
    fontSize: "0.8em",
    marginLeft: -2,
    marginRight: 5,
  },
  "&.icon-ref": {
    position: "relative",
    bottom: -1,
    left: -2,
    marginRight: 3,
  },
  "& svg.icon-ref-valid": {
    fill: ClinicColors.primary,
    width: 11,
    height: 11,
  },
  "& svg.icon-ref-wrong": {
    fill: "orange",
    width: 12,
    height: 12,
  },
});

const ValidateTextStyled = styled("div")(
  (props: { variant: "validated" | "error"; theme?: Theme }) => {
    return {
      display: "flex",
      gap: 6,
      marginBottom: 6,
      alignItems: "center",
      color: props.variant === "validated" ? "green" : "orange",
      fontSize: 13,
      "& svg": { fill: "currentcolor", width: 16, height: 16 },
    };
  },
);

const ShowMoreButton = styled(Button)({
  padding: 0,
  textTransform: "capitalize",
});

const TooltipContentStyle = styled(Paper)({
  maxWidth: 400,
  maxHeight: 380,
  overflow: "auto",
  borderRadius: 5,
  marginBottom: 5,
  flexDirection: "column",
  alignItems: "start",
  padding: 14,
  ...scrollableStyle,
});

const TooltipTitle = styled("div")({
  fontWeight: 600,
});

const TextualDataList = styled("ul")({
  paddingLeft: 18,
  fontSize: 13,
  margin: "6px 0",
});

const ReferencesList = styled("ul")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  listStyleType: "none",
  padding: 0,
  fontSize: 13,
});

const CloseButton = styled(IconButton)({
  position: "absolute",
  top: 4,
  right: 8,
  "& svg": {
    width: 20,
    height: 20,
  },
});

const TooltipArrow = styled("span")({
  width: "8px",
  height: "8px",
  position: "absolute",
  "&::before": {
    width: "0",
    height: "0",
    marginLeft: "-3px",
    content: "''",
    display: "block",
    borderStyle: "solid",
  },
  '.grounded-data-paragraph-tooltip[data-popper-placement*="top"] &': {
    bottom: "0",
    marginBottom: "-3px",
  },
  '.grounded-data-paragraph-tooltip[data-popper-placement*="top"] &::before': {
    borderColor: "#fff transparent transparent transparent",
    borderWidth: "8px 8px 0 8px",
  },
  '.grounded-data-paragraph-tooltip[data-popper-placement*="bottom"] &': {
    top: "0",
    marginTop: "-8px",
  },
  '.grounded-data-paragraph-tooltip[data-popper-placement*="bottom"] &::before': {
    borderColor: "transparent transparent #fff transparent",
    borderWidth: "0 8px 8px 8px",
  },
});

export const HighlightedSpan = styled("span")({
  backgroundColor: "rgb(204, 229, 255)",
  color: "#000000",
  borderRadius: "5px",
});

function isValid(severity: ErrorSeverityLevel): Validity {
  switch (severity) {
    case ErrorSeverityLevel.ERROR || ErrorSeverityLevel.MILD_WARNING:
      return Validity.invalid;
    case ErrorSeverityLevel.NOT_FOUND || ErrorSeverityLevel.INFO:
      return Validity.hidden;
    default:
      return Validity.valid;
  }
}

export function getPrefix() {
  const url = document.location.href;
  const re = /.*(w-[^/]*)\/.*/;
  const match = re.exec(url);
  return match[1];
}

export function linkTo(href: string, hashPrefix?: boolean) {
  return (hashPrefix ? "#/" : "") + getPrefix() + "/" + href;
}

const validationText = (severity: ErrorSeverityLevel) => {
  const valid = isValid(severity);
  if (valid === Validity.hidden) return "";

  return valid === Validity.valid ? (
    <ValidateTextStyled variant={"validated"}>
      <CheckCircleIcon /> Clinically validated
    </ValidateTextStyled>
  ) : (
    <ValidateTextStyled variant={"error"}>
      <ReportProblemOutlinedIcon /> Contradiction found
    </ValidateTextStyled>
  );
};

const TooltipContent = (props: ToolTipProps) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <CloseButton size={"small"} onClick={props.onClose}>
        <CloseIcon />
      </CloseButton>
      {validationText(props.connectionStrength.severity)}
      <TooltipTitle>{`${
        props.outputReferenceSection ? `[${props.supNumberIndex}] ` : ""
      }${props.statement.statement}`}</TooltipTitle>

      {props.outputReferenceSection
        ? groundingMatchingValidityText(props.statement)
        : ""}

      {props.textualData?.length > 0 && (
        <>
          <SafeCollapse inExtension in={expanded}>
            <TextualDataList>
              {props.textualData.map((d, i) => (
                <li key={"textualData" + i}>{d}</li>
              ))}
            </TextualDataList>
          </SafeCollapse>

          <ShowMoreButton
            size={"small"}
            color={"primary"}
            variant={"text"}
            onClick={() => {
              setExpanded((prevState) => !prevState);
              //props.onExpand();
            }}
          >
            {expanded ? "Show less" : "Show more"}
          </ShowMoreButton>
        </>
      )}
      {props.references?.length > 0 && (
        <ReferencesList>
          {props.references?.map((r, i) => (
            <li key={"reference" + i}>{r}</li>
          ))}
        </ReferencesList>
      )}
    </>
  );
};

export function GroundedDataParagraph({
  groundedData,
  outputReferenceSection,
  inExtension,
}: {
  groundedData: GroundingFragment;
  outputReferenceSection: boolean;
  inExtension?: boolean;
}) {
  const [highlightedWords, setHighlightedWords] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [tooltipArrowEl, setTooltipArrowEl] = useState(null);
  const [useKahunLinks, setUseKahunLinks] = useState<boolean>(true);

  // This state will hold the index of the currently active tooltip or null if no tooltip is active
  const [activeTooltip, setActiveTooltip] = useState<ToolTipProps>(null);

  //all text words - put in array
  let paragraphWords = groundedData.sentences.flatMap((groundedSentence) =>
    groundedSentence.sentence.split(" "),
  );

  let standaloneReferenceElements: JSX.Element[] = []; //elements to be output for [reference] section - if required.

  //add toggle

  standaloneReferenceElements.push(
    <FormControlLabel
      control={
        <Switch
          checked={useKahunLinks}
          onChange={() => setUseKahunLinks(!useKahunLinks)}
          color="primary"
        />
      }
      label={useKahunLinks ? "Use Kahun Links" : "Use PubMed Links"}
    />,
  );

  const supNumberTooltipsPerWordIndex: Record<number, JSX.Element[]> = {}; //Per wordIndex , there might be an array of tooltips for several supNumbers
  let latestReference = 0;

  // Run through sentences and construct text with sup-numbers
  for (let sentence of groundedData.sentences) {
    for (let statement of sentence.statements || []) {
      let supNumberIndex: number = statement.runningIndex; //reference id -- this is might be buggy, will fix later.

      let textualAndReferences = textAndReference(statement);

      //Add the references text to a standalone block
      if (outputReferenceSection && supNumberIndex > latestReference) {
        standaloneReferenceElements.push(
          <ListItemText>{`[${supNumberIndex}] ${statement.statement}`}</ListItemText>,
        );
        if (textualAndReferences) {
          standaloneReferenceElements.push(
            <TextualDataList>
              {textualAndReferences.textualWithReferences.map((e, i) =>
                textWithReferencesLine(e, true, useKahunLinks, i),
              )}
            </TextualDataList>,
          );
        }
        latestReference = supNumberIndex;
      }

      // add sup-number element that has tooltip on click to aggregated object.
      supNumberTooltipsPerWordIndex[statement.wordIndexForReferencePoint] =
        supNumberTooltipsPerWordIndex[statement.wordIndexForReferencePoint] ||
        [];
      supNumberTooltipsPerWordIndex[statement.wordIndexForReferencePoint].push(
        tooltipOfStatementSupNumbers(
          statement,
          supNumberIndex,
          textualAndReferences?.textualData,
          textualAndReferences?.references,
        ),
      );
    }
  }

  //Inject sup-numbers into the paragraph

  //first wrap each word with a span that can be highlighted if relevant sup-number were hovered.
  let spannedWords = paragraphWords.map((w, idx) =>
    highlightedWords?.includes(idx) ? (
      <HighlightedSpan key={"highlighted" + idx}>{w} </HighlightedSpan>
    ) : (
      <span key={"regular" + idx}>{w} </span>
    ),
  );

  //next to each word, if there's a relevant sup-number, append the sup-number to that word
  let spannedWordsWithSupNumbers = [];
  //5 - for citations at end of text
  for (let i = 0; i < spannedWords.length + 5; i++) {
    if (spannedWords[i]) spannedWordsWithSupNumbers.push(spannedWords[i]);
    if (supNumberTooltipsPerWordIndex[i]?.length) {
      //if there are several sup-numbers to append next to that word, aggregated them with space between each other
      let highlightRefsWithCommas = supNumberTooltipsPerWordIndex[i].map(
        (x, idx) =>
          idx > 0 ? (
            <React.Fragment key={`sup${i}_${idx}`}>
              <sup>,&nbsp;</sup> {x}
            </React.Fragment>
          ) : (
            x
          ),
      );
      // highlightRefsWithCommas.push(<span> </span>);

      spannedWordsWithSupNumbers.push(...highlightRefsWithCommas);
    }
  }

  const closeTooltip = () => {
    setAnchorEl(null);
    setActiveTooltip(null);
    setHighlightedWords([]);
  };

  return (
    <>
      {spannedWordsWithSupNumbers}
      {outputReferenceSection && <List> {standaloneReferenceElements}</List>}
      {activeTooltip && (
        <Popper
          className={"grounded-data-paragraph-tooltip"}
          anchorEl={anchorEl}
          placement={"top"}
          open={!!anchorEl}
          modifiers={[
            {
              name: "flip",
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: false,
                altBoundary: false,
                tether: false,
                rootBoundary: "document",
                padding: 8,
              },
            },
            {
              name: "arrow",
              enabled: true,
              options: {
                element: tooltipArrowEl,
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={closeTooltip}>
            <TooltipContentStyle elevation={5}>
              <TooltipArrow
                className={"tooltip-arrow-el"}
                ref={setTooltipArrowEl}
              />
              <TooltipContent
                {...activeTooltip}
                onClose={closeTooltip}
                outputReferenceSection={outputReferenceSection}
              />
            </TooltipContentStyle>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );

  //Create a React element of sup-numbers that is clickable and hoverable.
  // When hovering - it marks relevant words
  // when clicking - it shows a tooltip.
  function tooltipOfStatementSupNumbers(
    statement: GroundingStatements,
    supNumberIndex: number,
    textualData: string[],
    references: string[],
  ) {
    const connectionStrength = connectionStrengthToSeverity(
      statement.connectionStrength || ConnectionStrength.Strong,
      statement.hypothesis,
    );
    let referenceSeverity = maxSeverityLevel([
      connectionStrength.severity,
      entityMatchToSeverity(statement.elementA?.match)?.severity,
      entityMatchToSeverity(statement.elementB?.match)?.severity,
    ]);
    const valid = isValid(connectionStrength.severity);

    if (valid === Validity.hidden) return null;
    return (
      <ValidateStyle
        className={outputReferenceSection ? "numeric-ref" : "icon-ref"}
        key={"supNumber" + supNumberIndex}
        onClick={(e) => {
          setActiveTooltip({
            supNumberIndex,
            statement,
            textualData,
            references,
            connectionStrength,
            inExtension,
          });
          setHighlightedWords(statement.wordsIndices);
          console.log(e.currentTarget);
          setAnchorEl(e.currentTarget);
        }}
        onMouseOver={() => setHighlightedWords(statement.wordsIndices)}
        onMouseOut={() => {
          if (activeTooltip?.supNumberIndex !== supNumberIndex)
            setHighlightedWords([]);
        }}
        style={
          outputReferenceSection
            ? { color: errorSeverityLevelToColor(referenceSeverity.severity) }
            : null
        }
      >
        {outputReferenceSection ? (
          supNumberIndex
        ) : valid === Validity.valid ? (
          <ValidatedIcon className={"icon-ref-valid"} />
        ) : (
          <WarningIcon className={"icon-ref-wrong"} />
        )}
      </ValidateStyle>
    );
  }
}

function textWithReferencesLine(
  textualWithReferences: {
    textual: string;
    references: CitationDataFragment[];
  },
  addLinks: boolean,
  kahunLinks: boolean,
  index: number,
) {
  const textual = textualWithReferences.textual;
  const references = textualWithReferences.references;
  return (
    <li key={"textualData" + index}>
      {textual}
      {references?.length && addLinks && (
        <sup>
          {references
            .filter((r) => r)
            .map((r, i) => (
              <React.Fragment key={"cnum" + i}>
                {i > 0 ? `,` : ``}{" "}
                <Tooltip title={`${r.title} in ${r.containerTitle}`}>
                  <a
                    href={
                      kahunLinks
                        ? linkTo("citationEdit/" + r.kahunId, true)
                        : r.url
                    }
                    target={"_blank"}
                  >
                    {i + 1}
                  </a>
                </Tooltip>
              </React.Fragment>
            ))}
        </sup>
      )}
    </li>
  );
}
export function textAndReference(statement: GroundingStatements) {
  const textualWithReferences: {
    textual: string;
    references: CitationDataFragment[];
  }[] = [];
  statement.edgesWithTextualReferences.sort((a, b) => a.priority - b.priority);
  //show only three different priorities of edges (corresponding to three paths.)
  const priorities = new Set();
  let textualData: string[] = [];
  statement.edgesWithTextualReferences.forEach((e) => {
    priorities.add(e.priority);
    if (priorities.size > 3) return;
    const currentEdgeReferences = e.rvWithTextualReference.sort(
      (a, b) => a.priority - b.priority,
    );
    const currentEdgeTextualData = currentEdgeReferences?.[0]?.textual;
    if (currentEdgeTextualData?.length) {
      textualData.push(currentEdgeTextualData);
      textualWithReferences.push({
        textual: currentEdgeTextualData,
        references: currentEdgeReferences.flatMap(
          (ref) => ref.ruleValue.citationsObj,
        ),
      });
    }
  });

  const allReferences = statement.edgesWithTextualReferences
    .flatMap((e) =>
      e.rvWithTextualReference.flatMap(
        (rv) => rv.ruleValue?.citationsObj?.vancouver,
      ),
    )
    .filter((x) => x?.length);

  return {
    textualData: textualData,
    textualWithReferences,
    references: allReferences,
  };
}

export function topologicalSort(edges: PairGraphEdges[]): PairGraphEdges[] {
  if (!edges?.length) return null;
  const nodesToEdge = (n1, n2) =>
    edges.find((e) => e.sourceID === n1 && e.targetID === n2);
  const pathToEdges = (path) =>
    path
      .map((node, index, arr) =>
        index < arr.length - 1 ? nodesToEdge(node, arr[index + 1]) : null,
      )
      .filter((edge) => edge);
  let graph = edges.map((e) => [e.sourceID, e.targetID]);
  let sorted = null;

  while (!sorted) {
    try {
      sorted = toposort(graph);
    } catch (e) {
      const node = e?.message?.split("node was")?.[1]?.trim();
      if (!node) return null;
      const edge = edges.find(
        (e) =>
          (e.ruleValues?.length == 0 && // remove edge that  has no rulevalues if exists
            (e.sourceID == node || e.targetID == node)) ||
          e.targetID == node || //otherwise, remove some arbitrary edge
          e.sourceID == node,
      );
      if (!edge) return null;
      graph = edges
        .filter((e) => e != edge)
        .map((e) => [e.sourceID, e.targetID]);
    }
  }
  let sortedEdges = pathToEdges(sorted);
  if (sortedEdges?.length != edges.length) return null;
  return sortedEdges;
}

enum ErrorSeverityLevel {
  ERROR = "ERROR",
  NOT_FOUND = "NOT_FOUND",
  WARNING = "WARNING",
  MILD_WARNING = "MILD_WARNING",
  INFO = "INFO",
  OK = "OK",
}

export function errorSeverityLevelToNumber(severity: ErrorSeverityLevel) {
  switch (severity) {
    case ErrorSeverityLevel.ERROR:
      return 5;
    case ErrorSeverityLevel.NOT_FOUND:
      return 5;
    case ErrorSeverityLevel.WARNING:
      return 4;
    case ErrorSeverityLevel.MILD_WARNING:
      return 3;
    case ErrorSeverityLevel.INFO:
      return 2;
    case ErrorSeverityLevel.OK:
      return 1;
    default:
      return 0;
  }
}
export function maxSeverityLevel(severities: ErrorSeverityLevel[]) {
  return severities.reduce(
    (acc, s) => {
      if (acc.level >= errorSeverityLevelToNumber(s)) return acc;
      return { level: errorSeverityLevelToNumber(s), severity: s };
    },
    { severity: null, level: 0 },
  );
}

export function errorSeverityLevelToColor(severity: ErrorSeverityLevel) {
  switch (severity) {
    case ErrorSeverityLevel.ERROR:
      return "red";
    case ErrorSeverityLevel.NOT_FOUND:
      return "red";
    case ErrorSeverityLevel.WARNING:
      return "orange";
    case ErrorSeverityLevel.MILD_WARNING:
      return "yellow";
    case ErrorSeverityLevel.INFO:
      return "blue";
    case ErrorSeverityLevel.OK:
      return "green";
    default:
      return "black";
  }
}
export function entityMatchToSeverity(entityMatch: NodeMatch) {
  if (entityMatch === "EXACT") {
    return {
      severity: ErrorSeverityLevel.OK,
    };
  }
  if (entityMatch === NodeMatch.None) {
    return {
      severity: ErrorSeverityLevel.ERROR,
      text: `We couldn't find a relevant entity in our Knowledge Base`,
    };
  } else
    return {
      severity: ErrorSeverityLevel.WARNING,
      text: `We found connection to a similar entity in our Knowledge Base`,
    };
}
export function connectionStrengthToSeverity(
  strength: ConnectionStrength,
  hypothesis: StatementHypothesis,
): ConncectionStrength {
  if (
    strength == ConnectionStrength.Medium ||
    strength == ConnectionStrength.Strong
  ) {
    if (hypothesis != StatementHypothesis.DoesNotCause) {
      return {
        severity: ErrorSeverityLevel.OK,
        message: "Clinically validated by Kahun",
      };
    } else {
      return {
        severity: ErrorSeverityLevel.ERROR,
        message: `We found a ${strength.toLowerCase()} connection although the text implied that there is no connection`,
      };
    }
  } else if (strength == ConnectionStrength.Weak) {
    if (hypothesis == StatementHypothesis.DoesNotCause) {
      return {
        severity: ErrorSeverityLevel.MILD_WARNING,
        message: `We found a weak connection although the text implied that there is no connection`,
      };
    } else {
      return {
        severity: ErrorSeverityLevel.WARNING,
        message: `We found a weak connection although the text implied that there is a connection`,
      };
    }
  } else {
    //connection strength == none
    if (hypothesis != StatementHypothesis.DoesNotCause) {
      return {
        severity: ErrorSeverityLevel.NOT_FOUND,
        message: `We found no connection although the text implied that there is a connection`,
      };
    } else {
      return {
        severity: ErrorSeverityLevel.INFO,
      };
    }
  }
}
//Create a react component that displays the error\information messages of statement
// It might show info regarding the existance of connection, and existance of nodes.
export function groundingMatchingValidityText(statement: GroundingStatements) {
  let output = [];

  const connection = connectionStrengthToSeverity(
    statement.connectionStrength,
    statement.hypothesis,
  );

  const entityAMatch = entityMatchToSeverity(statement.elementA.match);
  const entityBMatch = entityMatchToSeverity(statement.elementB.match);

  const noConnectionToEntityA =
    statement.elementA.match == NodeMatch.None
      ? `${statement.elementA.element}`
      : null;
  const noConnectionToEntityB =
    statement.elementB.match == NodeMatch.None
      ? `${statement.elementB.element}`
      : null;
  const connectSentenceOfTwoEntities = (begining, entityA, entityB, ending) =>
    entityA || entityB
      ? `${begining} ${entityA || ""} ${
          entityA?.length && entityB?.length ? `and ` : ``
        } ${entityB || ""}`
      : null;

  const entityATextSimilarTypes =
    statement.elementA.match == NodeMatch.Subtype
      ? ` type of ${statement.elementA.element}`
      : statement.elementA.match == NodeMatch.Sibling ||
        statement.elementA.match == NodeMatch.Supertype
      ? ` other form of ${statement.elementA.element}`
      : ``;
  const entityBTextSimilarTypes =
    statement.elementB.match == NodeMatch.Subtype
      ? ` type of ${statement.elementB.element}`
      : statement.elementB.match == NodeMatch.Sibling ||
        statement.elementB.match == NodeMatch.Supertype
      ? ` other form of ${statement.elementB.element}`
      : ``;

  const couldntFindEntity = connectSentenceOfTwoEntities(
    `We could not find the entity`,
    noConnectionToEntityA,
    noConnectionToEntityB,
    `in our knowledge base`,
  );
  const foundSomeConnection = connectSentenceOfTwoEntities(
    `We found a connection to`,
    entityATextSimilarTypes,
    entityBTextSimilarTypes,
    `in our knowledge base`,
  );

  const maxSeverity = maxSeverityLevel([
    entityAMatch.severity,
    entityBMatch.severity,
  ]);
  const maxSeverityEntity =
    entityAMatch.severity == maxSeverity.severity ? entityAMatch : entityBMatch;

  if (couldntFindEntity?.length) {
    //We couldn't find one of the entities in db so no connection anyway

    return (
      <Typography
        variant="body1"
        style={{
          marginBottom: "5px",
          color: errorSeverityLevelToColor(maxSeverityEntity.severity),
          fontSize: "14px",
        }}
        paragraph
      >
        {couldntFindEntity}
      </Typography>
    );
  }

  if (connection.severity != ErrorSeverityLevel.OK) {
    const color = errorSeverityLevelToColor(connection.severity);
    output.push(
      <Typography
        variant="body1"
        style={{
          color,
          fontSize: "14px",
          paddingLeft: "14px",
          marginBottom: "5px",
        }}
        paragraph
      >
        {connection.message}
      </Typography>,
    );
  }

  if (
    maxSeverityEntity.severity != ErrorSeverityLevel.OK &&
    statement.connectionStrength != ConnectionStrength.None // show information regarding subtypes of entities only if there is some connection
  ) {
    const color = errorSeverityLevelToColor(maxSeverityEntity.severity);
    output.push(
      <Typography
        variant="body1"
        style={{
          color,
          fontSize: "14px",
          paddingLeft: "14px",
          marginBottom: "5px",
        }}
        paragraph
      >
        {foundSomeConnection}
      </Typography>,
    );
  }

  return <div>{output}</div>;
}
